/**
 * How one ListEventAttendees row reads in the Attendance List.
 *
 * An attendee is a Customer Link campaign member of the event's SFDC campaign, joined by the
 * service with the Event Hub nomination for that guest when there is one. The event's nominations
 * come separately, all of them on the first page, and each becomes a row of its own with only
 * `nomination` (see {@link mergeAttendees}). So every field reads the campaign member's own value
 * first and falls back to the nomination's.
 *
 * Here rather than in the component because the coverage config measures only `.ts`.
 */
import type {
  CampaignMembership,
  EventAttendee,
  Nomination,
  NominationStatus,
} from '../client/EventCalendarClient';
import { displayName, guestNameOf } from './guestName';
import { canonicalJobTitle } from './jobTitles';
import { NOMINATION_STATUS_LABELS, NOMINATION_STATUS_ORDER } from './nominationStatus';
import { isAttendanceConfirmed } from './attendance';
import { columnLabel, columnsFor, nominationCellText } from './nominationColumns';
import { ATTENDANCE_CONFIRMED } from './nominationCounts';
import { listedMemberStatus } from './attendeeMerger';

/** The row's identity for the table: the campaign member, else its nomination. */
export function attendeeKey(a: EventAttendee): string {
  return a.campaignMemberId || a.nomination?.nominationId || '';
}

export function attendeeName(a: EventAttendee): string | undefined {
  return displayName(a.firstName, a.lastName) ?? guestNameOf(a.nomination?.guestInformation);
}

/** The SFDC account ID — the Account ID column. */
export function attendeeAccountId(a: EventAttendee): string | undefined {
  return a.sfdcAccountId || a.nomination?.accountInformation?.sfdcAccountId || undefined;
}

/**
 * The account name: the member's SFDC account name, else the company name the member or its
 * nomination carries — the nomination has no SFDC account name of its own.
 */
export function attendeeAccountName(a: EventAttendee): string | undefined {
  return (
    a.sfdcAccountName ||
    a.companyName ||
    a.nomination?.accountInformation?.companyName ||
    undefined
  );
}

/**
 * The job title and job role. ListEventAttendees now returns the Customer Link lead/contact's
 * `title` and `jobRole` on the member itself, as currently stored there; those win. The
 * nomination's `guestInformation`, captured at nomination time, is the fallback for a row whose
 * member has none — including a nomination not (yet) in the campaign.
 */
export function attendeeJobTitle(a: EventAttendee): string | undefined {
  return a.jobTitle || a.nomination?.guestInformation?.jobTitle || undefined;
}

export function attendeeJobRole(a: EventAttendee): string | undefined {
  return a.jobRole || a.nomination?.guestInformation?.jobRole || undefined;
}

/** The phone: the campaign member's, else the one the BD entered on the nomination. */
export function attendeePhone(a: EventAttendee): string | undefined {
  return a.phone || a.nomination?.guestInformation?.phoneNumber || undefined;
}

/** The email: the campaign member's, else the one the BD entered on the nomination. */
export function attendeeEmail(a: EventAttendee): string | undefined {
  return a.email || a.nomination?.guestInformation?.email || undefined;
}

/** A nomination with no campaign member behind it: the guest is nominated, not registered. */
export function isNominationOnly(a: EventAttendee): boolean {
  return !a.campaignMemberId && !!a.nomination;
}

/**
 * Registration Status: the campaign member status read as registration. Event registration sets
 * "Registered" or "Attended"; Attended implies registered. Any other SFDC status (Prospect, Sent,
 * Responded, …) is shown as given — the service passes it through as a string, and an unknown one
 * is information, not an error.
 *
 * A nomination-only row reads "Nominated · <review status>" rather than a dash. There is no
 * registration to report — Customer Link only has one once the guest registers — but a dash reads
 * as missing data, where this says what the guest is: nominated, not registered yet. Attendance
 * Status stays a dash; it is too early for anything to be said there.
 */
export function registrationStatus(a: EventAttendee): string | undefined {
  const s = a.campaignMemberStatus?.trim();
  if (!s) {
    const status = a.nomination?.status;
    return isNominationOnly(a) && status ? `Nominated · ${NOMINATION_STATUS_LABELS[status]}` : undefined;
  }
  const k = s.toLowerCase();
  if (k === 'registered' || k === 'attended') return 'Registered';
  return s;
}

/**
 * Attendance Status: Attended or Registered, the only campaign member statuses listed (PM rule,
 * utils/attendeeMerger drops the rest); for a merged person, Attended when any of their
 * memberships is. A nomination with no campaign member behind it has none, and reads as a dash.
 */
export function attendanceStatus(a: EventAttendee): string | undefined {
  if (isNominationOnly(a)) return undefined;
  return listedMemberStatus(a.campaignMemberStatus);
}

/**
 * The Attendance List's rows: the event's nominations first, then the campaign members.
 *
 * ListEventAttendees returns every nomination on its first page and pages through the members
 * after, so the nominations are all there at once while the members keep arriving. Putting them
 * first means the rows a BD or marketer acts on (review, 确认出席) are on the first page of the
 * table and stay put as later pages load.
 *
 * A member the service matched to nominations carries them as its `nominations` (a merged person
 * from utils/attendeeMerger, all of theirs across campaigns; before the service sent the list,
 * its one `nomination`). That member takes the row of the first of them, in that nomination's
 * place, and the rest of its nominations' rows go — rather than listing the guest twice. The row
 * then shows the member's own values and campaign status, with the nominations behind them. A
 * member none of whose nominations is in the list (or whose ones are already taken) is appended
 * as is.
 *
 * So until the last page has loaded, a nomination row may still be claimed by a member on a later
 * page: the nomination-only rows are final only then.
 */
export function mergeAttendees(
  nominations: readonly Nomination[],
  attendees: readonly EventAttendee[],
): EventAttendee[] {
  const rows: (EventAttendee | undefined)[] = nominations.map((n) => ({
    campaignMemberId: '',
    nomination: n,
  }));
  const slot = new Map<string, number>();
  nominations.forEach((n, i) => {
    if (n.nominationId && !slot.has(n.nominationId)) slot.set(n.nominationId, i);
  });
  for (const a of attendees) {
    const ids = (a.nominations ?? (a.nomination ? [a.nomination] : []))
      .map((n) => n.nominationId)
      .filter((id): id is string => !!id && slot.has(id));
    if (ids.length === 0) {
      rows.push(a);
      continue;
    }
    const slots = ids.map((id) => slot.get(id)!).sort((x, y) => x - y);
    rows[slots[0]] = a;
    for (const i of slots.slice(1)) rows[i] = undefined;
    for (const id of ids) slot.delete(id);
  }
  return rows.filter((r): r is EventAttendee => !!r);
}

/**
 * The Attendance List's status filter. `ACTIVE`, the default, is every row but a rejected
 * nomination nobody registered behind: ListEventAttendees returns rejected nominations on purpose,
 * but a guest turned down and never registered is not attending, and listing them under
 * "Attendance" would mislead. A rejected nomination joined to a campaign member stays — that guest
 * registered anyway. `ALL` shows them too; a status shows the rows whose nomination has it.
 */
export type AttendeeStatusFilter = 'ACTIVE' | 'ALL' | NominationStatus;

export const ATTENDEE_STATUS_FILTERS: readonly { value: AttendeeStatusFilter; label: string }[] = [
  { value: 'ACTIVE', label: 'All but rejected' },
  { value: 'ALL', label: 'All' },
  ...NOMINATION_STATUS_ORDER.map((s) => ({ value: s, label: NOMINATION_STATUS_LABELS[s] })),
];

export function matchesStatusFilter(a: EventAttendee, filter: AttendeeStatusFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'ACTIVE') return !(isNominationOnly(a) && a.nomination?.status === 'REJECTED');
  return a.nomination?.status === filter;
}

/**
 * The Attendance List's other dropdown filters, beside Nomination Status. Each is "All" (the
 * {@link ATTENDEE_FILTER_ALL} sentinel, so an empty field cannot match a real value) or one value.
 *
 *   - Customer Business Unit is the guest's BU as the Customer BU column shows it
 *     ({@link attendeeBu}) — not the BD's BU. The options are whatever the rows carry, trimmed. A
 *     row with none is left out once one is picked.
 *   - Registration Status filters on what that column shows, except that a nomination-only row's
 *     "Nominated · <review status>" is one value, "Nominated": the review status already has its
 *     own filter.
 *   - Attendance Confirmed matches the Nomination List's: Confirmed / Not confirmed, of the rows
 *     with a nomination — there is nothing to confirm for a guest nobody nominated.
 *   - Attendance Status filters on what that column shows: the campaign member status as given.
 *
 * BU, Registration Status and Attendance Status offer the values the rows carry, so an option
 * never matches nothing.
 */
export const ATTENDEE_FILTER_ALL = '__ALL__';
export const NOMINATED = 'Nominated';

export interface AttendeeFilters {
  bu: string;
  registration: string;
  /** ATTENDANCE_CONFIRMED / ATTENDANCE_UNCONFIRMED from utils/nominationCounts, or All. */
  confirmed: string;
  /** A campaign member status (see {@link attendanceStatus}), or All. */
  attendance: string;
}

export const NO_ATTENDEE_FILTERS: AttendeeFilters = {
  bu: ATTENDEE_FILTER_ALL,
  registration: ATTENDEE_FILTER_ALL,
  confirmed: ATTENDEE_FILTER_ALL,
  attendance: ATTENDEE_FILTER_ALL,
};

/**
 * The guest's BU — the Customer BU column, and what the Customer Business Unit filter matches.
 * The campaign member's `salesHierarchySubBusinessUnit` (the GCR sub BU of the member's territory,
 * from ListEventAttendees) wins; the nomination's `guestInformation.businessUnit` (auto-filled from
 * the contact's SFDC account sub BU, or typed by the BD) is the fallback for a row whose member has
 * none — including a nomination not (yet) in the campaign. Member first, as for job title. Trimmed.
 * Not the BD's BU.
 */
export function attendeeBu(a: EventAttendee): string | undefined {
  return (
    a.salesHierarchySubBusinessUnit?.trim() ||
    a.nomination?.guestInformation?.businessUnit?.trim() ||
    undefined
  );
}

/** Registration Status as the filter reads it (see {@link AttendeeFilters}). */
export function registrationFilterValue(a: EventAttendee): string | undefined {
  if (!a.campaignMemberStatus?.trim() && isNominationOnly(a)) return NOMINATED;
  return registrationStatus(a);
}

function distinctOptions(values: readonly (string | undefined)[]): { value: string; label: string }[] {
  return [...new Set(values.filter((v): v is string => !!v))]
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

/** The Customer Business Unit filter's values: every Customer BU among the rows, sorted. */
export function buFilterOptions(attendees: readonly EventAttendee[]) {
  return distinctOptions(attendees.map(attendeeBu));
}

/** The Attendance Status filter's values: every campaign member status among the rows, sorted. */
export function attendanceFilterOptions(attendees: readonly EventAttendee[]) {
  return distinctOptions(attendees.map(attendanceStatus));
}

/** The Registration Status filter's values: every one among the rows, sorted. */
export function registrationFilterOptions(attendees: readonly EventAttendee[]) {
  return distinctOptions(attendees.map(registrationFilterValue));
}

/**
 * Whether a row passes the filters. `keepConfirmed` is the nominations whose attendance the reader
 * ticked or unticked since choosing the Attendance Confirmed filter: they stay, as on the
 * Nomination List, so a row does not vanish under the reader's hand.
 */
export function matchesAttendeeFilters(
  a: EventAttendee,
  f: AttendeeFilters,
  keepConfirmed: ReadonlySet<string> = new Set(),
): boolean {
  if (f.bu !== ATTENDEE_FILTER_ALL && attendeeBu(a) !== f.bu) return false;
  if (f.registration !== ATTENDEE_FILTER_ALL && registrationFilterValue(a) !== f.registration) {
    return false;
  }
  if (f.confirmed !== ATTENDEE_FILTER_ALL) {
    const n = a.nomination;
    if (!n) return false;
    if (
      !keepConfirmed.has(n.nominationId ?? '') &&
      isAttendanceConfirmed(n) !== (f.confirmed === ATTENDANCE_CONFIRMED)
    ) {
      return false;
    }
  }
  if (f.attendance !== ATTENDEE_FILTER_ALL) {
    if (attendanceStatus(a) !== f.attendance) return false;
  }
  return true;
}

/** The attendees with one nomination swapped for the version the service just returned. */
export function withNomination(
  attendees: readonly EventAttendee[],
  updated: Nomination,
): EventAttendee[] {
  const same = (n: Nomination) => n.nominationId === updated.nominationId;
  return attendees.map((a) => {
    const inOne = !!a.nomination && same(a.nomination);
    const inList = !!a.nominations?.some(same);
    if (!inOne && !inList) return a;
    return {
      ...a,
      ...(inOne ? { nomination: updated } : {}),
      ...(inList ? { nominations: a.nominations!.map((n) => (same(n) ? updated : n)) } : {}),
    };
  });
}

/**
 * The campaigns a person is a member of (with sub-campaigns, the event's own and those beneath
 * it), in listing order — the event's own first. Empty before the service sent memberships.
 */
export function attendeeMemberships(a: EventAttendee): readonly CampaignMembership[] {
  return a.memberships ?? [];
}

/** One membership's campaign name; its SFDC campaign id when Customer Link has no name. */
export function membershipText(m: CampaignMembership): string {
  return m.sfdcCampaignName?.trim() || m.sfdcCampaignId || '(unnamed campaign)';
}

/**
 * The Campaigns column as one cell of text. Joined with "; " rather than split back apart
 * anywhere: a campaign name can contain a comma.
 */
export function campaignsCellText(a: EventAttendee): string | undefined {
  const ms = attendeeMemberships(a);
  return ms.length ? ms.map(membershipText).join('; ') : undefined;
}

/** The SFDC Campaign ID column as one cell of text, in the Campaigns column's order. */
export function campaignIdsCellText(a: EventAttendee): string | undefined {
  const ids = attendeeMemberships(a)
    .map((m) => m.sfdcCampaignId)
    .filter((id): id is string => !!id);
  return ids.length ? ids.join('; ') : undefined;
}

/**
 * The text of one Attendance List column for an attendee — what the table shows, as text. The CSV
 * export is built from this, over the same column list, so the file and the table agree.
 */
export function attendeeCellText(id: string, a: EventAttendee): string | undefined {
  const n = a.nomination;
  switch (id) {
    case 'guest':
      return attendeeName(a);
    case 'company':
      return attendeeAccountName(a);
    case 'jobTitle':
      return canonicalJobTitle(attendeeJobTitle(a));
    case 'jobRole':
      return canonicalJobTitle(attendeeJobRole(a));
    case 'phone':
      return attendeePhone(a);
    case 'email':
      return attendeeEmail(a);
    case 'bdName':
      return n?.bdName || n?.createdBy || undefined;
    case 'status':
      return n?.status ? NOMINATION_STATUS_LABELS[n.status] : undefined;
    case 'registration':
      return registrationStatus(a);
    case 'confirmed':
      return n && isAttendanceConfirmed(n) ? 'Confirmed' : undefined;
    case 'attendance':
      return attendanceStatus(a);
    case 'sfdcAccountId':
      return attendeeAccountId(a);
    case 'customerBu':
      return attendeeBu(a);
    case 'campaigns':
      return campaignsCellText(a);
    case 'sfdcCampaignId':
      return campaignIdsCellText(a);
    default:
      return nominationCellText(id, n);
  }
}

/**
 * The guest's Customer Link record: the member's, else the one the nomination was made from. The
 * id the service joins a member to its nomination on — no use to a reader, so not a table column,
 * but in the CSV for working out why a guest shows twice or not at all.
 */
export function attendeeObjectId(a: EventAttendee): string | undefined {
  return a.objectId || a.nomination?.guestInformation?.objectId || undefined;
}

export function attendeeObjectType(a: EventAttendee): string | undefined {
  return a.objectType || a.nomination?.guestInformation?.objectType || undefined;
}

/**
 * The Attendance List as CSV: every column, in the table's order, under the table's names, then
 * the Customer Link record the row was joined on (export only, see {@link attendeeObjectId}).
 */
export function attendeeCsv(attendees: readonly EventAttendee[]): {
  headers: string[];
  rows: string[][];
} {
  const columns = columnsFor('attendance');
  return {
    headers: [
      ...columns.map((c) => columnLabel(c.id)),
      'Customer Link Object ID',
      'Customer Link Object Type',
    ],
    rows: attendees.map((a) => [
      ...columns.map((c) => attendeeCellText(c.id, a) ?? ''),
      attendeeObjectId(a) ?? '',
      attendeeObjectType(a) ?? '',
    ]),
  };
}
